import React from "react";
import { motion } from "motion/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowsRotate,
  faCircleCheck,
  faCircleExclamation,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";

import { Button } from "@/components/ui/button/Button";
import { useMcpCatalog } from "@/hooks/useMcpCatalog";
import type { McpServerDebugInfo } from "@/services/api/in-app-agent";

interface AniMcpDebugPanelProps {
  enabled: boolean;
  /** When true, omit outer card (parent DeveloperMenuSection provides the container). */
  embedded?: boolean;
}

function ServerStatus({ server }: { server: McpServerDebugInfo }) {
  const ready = server.enabled && server.configured && server.connected && !server.discovery_error;
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
        <FontAwesomeIcon
          icon={ready ? faCircleCheck : faCircleExclamation}
          className={ready ? "text-emerald-500" : "text-amber-500"}
          aria-hidden="true"
        />
        <span>
          {server.enabled ? "enabled" : "disabled"} · {server.configured ? "configured" : "not configured"} ·{" "}
          {server.connected ? "connected" : "not connected"}
          {server.discovery_error ? ` · ${server.discovery_error}` : ""}
        </span>
      </div>
      {server.missing_config_fields && server.missing_config_fields.length > 0 ? (
        <p className="text-[10px] text-amber-400/90">Missing: {server.missing_config_fields.join(", ")}</p>
      ) : null}
    </div>
  );
}

function ConfigurationPreview({ configuration }: { configuration: Record<string, string> }) {
  const entries = Object.entries(configuration);
  if (!entries.length) {
    return null;
  }
  return (
    <dl className="mt-2 space-y-1 rounded-md bg-slate-900/40 px-2 py-2">
      {entries.map(([key, value]) => (
        <div key={key} className="grid grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] gap-2 text-[10px]">
          <dt className="truncate font-mono text-slate-500">{key}</dt>
          <dd className="truncate font-mono text-slate-300" title={value}>
            {value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export function AniMcpDebugPanel({ enabled, embedded = false }: AniMcpDebugPanelProps) {
  const { data, isLoading, isError, refetch, isFetching } = useMcpCatalog(enabled);

  const content = (
    <>
      <div className={`flex items-center justify-end gap-2 ${embedded ? "mb-2" : ""}`}>
        <Button
          type="button"
          plain
          className="!px-2 !py-1 text-xs"
          onClick={() => void refetch()}
          disabled={!enabled || isFetching}
          aria-label="Refresh MCP catalog"
        >
          <FontAwesomeIcon icon={faArrowsRotate} className={isFetching ? "animate-spin" : ""} />
          Refresh
        </Button>
      </div>

      {!enabled ? (
        <p className="text-xs text-slate-500">Enable Developer menu as admin to inspect MCP servers.</p>
      ) : isLoading ? (
        <p className="text-xs text-slate-500">Loading MCP catalog…</p>
      ) : isError ? (
        <p className="text-xs text-red-400">Couldn&apos;t load MCP catalog. Try again.</p>
      ) : !data?.servers.length ? (
        <p className="text-xs text-slate-500">No MCP servers registered for this tenant.</p>
      ) : (
        <div className="space-y-3">
          {data.servers.map((server) => (
            <div key={server.connector_id} className="rounded-lg border border-slate-700/40 p-3">
              <div className="mb-1 flex items-center justify-between gap-2">
                <span className="font-medium text-sm text-white">{server.connector_id}</span>
                <span className="truncate text-[10px] text-slate-400">{server.url}</span>
              </div>
              <ServerStatus server={server} />
              {server.configuration ? <ConfigurationPreview configuration={server.configuration} /> : null}
              {server.tools.length > 0 ? (
                <ul className="mt-2 space-y-1">
                  {server.tools.map((tool) => (
                    <li key={tool.tool_id} className="text-[11px] text-slate-300">
                      <span className="font-mono text-slate-400">{tool.tool_id}</span>
                      <span className="text-slate-500"> → </span>
                      <span>{tool.mcp_tool_name || tool.display_name}</span>
                      <span className="text-slate-500">
                        {tool.allowlisted ? " · allowlisted" : ""}
                        {tool.discovered ? " · discovered" : " · not discovered"}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-2 text-[11px] text-slate-500">No tools discovered.</p>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );

  if (embedded) {
    return <div data-testid="ani-mcp-debug-panel">{content}</div>;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col gap-2 overflow-hidden"
    >
      <div className="rounded-xl bg-slate-800/30 px-4 py-3 dark:border-slate-600">{content}</div>
    </motion.div>
  );
}
